import { useMemo } from 'react';
import type { Hotspot, Stage } from '../../data/notationHotspots';
import {
  sensoryHotspots,
  integrationHotspots,
  symbolicHotspots,
  layerInfo
} from '../../data/notationHotspots';
import { HumanFigure } from './HumanFigure';
import './ExperienceScene.css';

interface ExperienceSceneProps {
  activeHotspot: Hotspot | null;
  exploredIds: Set<string>;
  currentStage: Stage;
  onSelectHotspot: (id: string) => void;
}

// Position symbolic objects evenly around the figure
const sceneObjectPositions: Record<string, { x: number; y: number; connectTo: { x: number; y: number } }> = {
  // Left side
  'phone':       { x: 7,  y: 14, connectTo: { x: 38, y: 28 } },
  'social-feed': { x: 5,  y: 40, connectTo: { x: 38, y: 42 } },
  'book':        { x: 7,  y: 64, connectTo: { x: 42, y: 58 } },
  'checklist':   { x: 9,  y: 86, connectTo: { x: 44, y: 78 } },
  // Right side
  'mirror':      { x: 93, y: 10, connectTo: { x: 62, y: 22 } },
  'calendar':    { x: 91, y: 34, connectTo: { x: 62, y: 40 } },
  'chalkboard':  { x: 93, y: 56, connectTo: { x: 62, y: 52 } },
  'dollar':      { x: 91, y: 76, connectTo: { x: 58, y: 68 } },
  // Bottom center
  'compass':     { x: 50, y: 90, connectTo: { x: 50, y: 82 } },
};

export function ExperienceScene({
  activeHotspot,
  exploredIds,
  currentStage,
  onSelectHotspot
}: ExperienceSceneProps) {
  // Determine active layer for figure highlighting
  const activeLayer = currentStage === 1 ? 'sensory' as const
    : currentStage === 2 ? 'integration' as const
    : currentStage === 3 ? 'symbolic' as const
    : null;

  // Only show hotspots for the CURRENT stage
  const bodyHotspots = useMemo(() => {
    if (currentStage === 1) return sensoryHotspots;
    if (currentStage === 2) return integrationHotspots;
    return [];
  }, [currentStage]);

  // Symbolic objects only shown during stage 3
  const visibleSymbolic = useMemo(() => {
    if (currentStage === 3) return symbolicHotspots;
    return [];
  }, [currentStage]);

  return (
    <div className="experience-scene">
      {/* Scene grid/background */}
      <div className="experience-scene__grid"></div>

      {/* Connection lines SVG */}
      <svg className="experience-scene__connections" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0, 6 3, 0 6" fill="var(--color-accent-secondary)" opacity="0.6" />
          </marker>
        </defs>

        {/* Connection lines for symbolic objects */}
        {visibleSymbolic.map(hotspot => {
          const pos = sceneObjectPositions[hotspot.id];
          if (!pos) return null;
          const isActive = activeHotspot?.id === hotspot.id;
          const isExplored = exploredIds.has(hotspot.id);

          return (
            <g key={`conn-${hotspot.id}`} className={`connection ${isActive ? 'connection--active' : ''}`}>
              <line
                x1={pos.x}
                y1={pos.y}
                x2={pos.connectTo.x}
                y2={pos.connectTo.y}
                stroke={isActive ? layerInfo.symbolic.color : 'var(--color-border)'}
                strokeWidth={isActive ? 0.4 : 0.2}
                strokeDasharray={isActive ? "1,0.5" : "0.5,1"}
                opacity={isActive ? 0.9 : isExplored ? 0.4 : 0.2}
                markerEnd={isActive ? "url(#arrowhead)" : ""}
              />
              {isActive && (
                <circle
                  cx={pos.connectTo.x}
                  cy={pos.connectTo.y}
                  r="1.5"
                  fill={layerInfo.symbolic.color}
                  className="connection__endpoint"
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* Symbolic objects arranged around scene */}
      <div className="experience-scene__objects">
        {visibleSymbolic.map(hotspot => {
          const pos = sceneObjectPositions[hotspot.id];
          if (!pos) return null;
          const isActive = activeHotspot?.id === hotspot.id;
          const isExplored = exploredIds.has(hotspot.id);

          return (
            <button
              key={hotspot.id}
              className={`scene-object ${isActive ? 'scene-object--active' : ''} ${isExplored ? 'scene-object--explored' : ''}`}
              style={{
                left: `${pos.x}%`,
                top: `${pos.y}%`,
              }}
              onClick={() => onSelectHotspot(hotspot.id)}
              aria-label={hotspot.label}
            >
              <span className="scene-object__glow"></span>
              <span className="scene-object__icon">
                <i className={`fas ${hotspot.icon}`}></i>
              </span>
              <span className="scene-object__label">{hotspot.label}</span>
              {isExplored && (
                <span className="scene-object__check">
                  <i className="fas fa-check"></i>
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Human figure container (centered) */}
      <div className="experience-scene__figure-area">
        <div className="experience-scene__figure">
          <HumanFigure activeLayer={activeLayer} />
        </div>

        {/* Body hotspots for current stage only */}
        <div className="experience-scene__body-hotspots">
          {bodyHotspots.map(hotspot => {
            const isActive = activeHotspot?.id === hotspot.id;
            const isExplored = exploredIds.has(hotspot.id);
            const layerColor = layerInfo[hotspot.layer].color;

            return (
              <button
                key={hotspot.id}
                className={`body-hotspot ${isActive ? 'body-hotspot--active' : ''} ${isExplored ? 'body-hotspot--explored' : ''}`}
                style={{
                  left: `${hotspot.position.xPct}%`,
                  top: `${hotspot.position.yPct}%`,
                  '--hotspot-color': layerColor
                } as React.CSSProperties}
                onClick={() => onSelectHotspot(hotspot.id)}
                aria-label={hotspot.label}
              >
                <span className="body-hotspot__pulse"></span>
                <span className="body-hotspot__dot">
                  <i className={`fas ${hotspot.icon}`}></i>
                </span>
                <span className="body-hotspot__check">
                  <i className="fas fa-check"></i>
                </span>
                <span className="body-hotspot__tooltip">
                  <span className="body-hotspot__layer" style={{ background: layerColor }}>
                    {layerInfo[hotspot.layer].name}
                  </span>
                  <strong>{hotspot.label}</strong>
                  <em>{hotspot.short}</em>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Current stage label */}
      <div className="experience-scene__layer-labels">
        {currentStage === 1 && (
          <span className="layer-label layer-label--sensory">
            <i className="fas fa-wave-square"></i> Sensory
          </span>
        )}
        {currentStage === 2 && (
          <span className="layer-label layer-label--integration">
            <i className="fas fa-brain"></i> Integration
          </span>
        )}
        {currentStage === 3 && (
          <span className="layer-label layer-label--symbolic">
            <i className="fas fa-icons"></i> Symbolic
          </span>
        )}
      </div>
    </div>
  );
}
